import {
  type ReactNode,
  type Ref,
  type TransitionEventHandler,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";

interface LayerRegistration {
  lockScroll: boolean;
  onEscape(): void;
  onTab(event: KeyboardEvent): void;
  restoreFocus?: HTMLElement | null;
  root?: HTMLElement | null;
}

const layerStack: LayerRegistration[] = [];

function handleLayerKeyDown(event: KeyboardEvent) {
  const layer = layerStack.at(-1);
  if (!layer) return;
  if (event.key === "Escape") {
    event.preventDefault();
    layer.onEscape();
    return;
  }
  if (event.key === "Tab") layer.onTab(event);
}

function registerOverlayLayer(options: LayerRegistration): { release(): void } {
  if (layerStack.length === 0) document.addEventListener("keydown", handleLayerKeyDown);
  layerStack.push(options);
  const releaseScrollLock = options.lockScroll ? acquireBodyScrollLock() : undefined;
  let released = false;
  return {
    release() {
      if (released) return;
      released = true;
      const index = layerStack.indexOf(options);
      if (index !== -1) {
        const wasTopLayer = index === layerStack.length - 1;
        const nextDialogLayer = layerStack
          .slice(index + 1)
          .find(
            (layer) => layer.restoreFocus && options.root?.contains(layer.restoreFocus) === true,
          );
        if (nextDialogLayer) nextDialogLayer.restoreFocus = options.restoreFocus;
        layerStack.splice(index, 1);
        if (wasTopLayer && options.restoreFocus?.isConnected) {
          options.restoreFocus.focus({ preventScroll: true });
        }
      }
      releaseScrollLock?.();
      if (layerStack.length === 0) document.removeEventListener("keydown", handleLayerKeyDown);
    },
  };
}

let scrollLockCount = 0;
let savedBodyOverflow = "";
let savedBodyPaddingRight = "";

function acquireBodyScrollLock(): () => void {
  if (scrollLockCount === 0) {
    const body = document.body;
    savedBodyOverflow = body.style.overflow;
    savedBodyPaddingRight = body.style.paddingRight;
    const scrollbarWidth =
      document.documentElement.clientWidth > 0
        ? window.innerWidth - document.documentElement.clientWidth
        : 0;
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    body.style.overflow = "hidden";
  }
  scrollLockCount += 1;
  return () => {
    scrollLockCount -= 1;
    if (scrollLockCount === 0) {
      document.body.style.overflow = savedBodyOverflow;
      document.body.style.paddingRight = savedBodyPaddingRight;
    }
  };
}

interface OverlayLayerOptions {
  onEscape(): void;
  onTab?(event: KeyboardEvent): void;
}

/** Joins a non-dialog surface, such as a dropdown, to the shared Escape stack. */
export function useOverlayLayer(open: boolean, options: OverlayLayerOptions): void {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useLayoutEffect(() => {
    if (!open) return;
    return registerOverlayLayer({
      lockScroll: false,
      onEscape: () => optionsRef.current.onEscape(),
      onTab: (event) => optionsRef.current.onTab?.(event),
    }).release;
  }, [open]);
}

type OverlayName =
  | { ariaLabel: string; ariaLabelledBy?: never }
  | { ariaLabel?: never; ariaLabelledBy: string };

type OverlayElement = "aside" | "div" | "section";

type OverlayProps = OverlayName & {
  as?: OverlayElement;
  ariaDescribedBy?: string;
  backdropClassName: string;
  backdropLabel: string;
  children: ReactNode;
  dismissDisabled?: boolean;
  onClose(): void;
  onTransitionEnd?: TransitionEventHandler<HTMLElement>;
  open: boolean;
  /** Keeps an exiting surface mounted after it leaves the interactive layer stack. */
  present?: boolean;
  role?: "alertdialog" | "dialog";
  rootClassName: string;
  surfaceClassName: string;
  surfaceRef?: Ref<HTMLElement>;
};

/**
 * Modal substrate. It owns the dialog element and its complete interaction
 * contract; callers supply only the visual classes required by their surface.
 */
export function Overlay({
  as = "section",
  ariaDescribedBy,
  ariaLabel,
  ariaLabelledBy,
  backdropClassName,
  backdropLabel,
  children,
  dismissDisabled,
  onClose,
  onTransitionEnd,
  open,
  present = open,
  role = "dialog",
  rootClassName,
  surfaceClassName,
  surfaceRef,
}: OverlayProps) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissDisabledRef = useRef(dismissDisabled);
  dismissDisabledRef.current = dismissDisabled;
  const setSurfaceRef = useCallback(
    (node: HTMLElement | null) => {
      dialogRef.current = node;
      assignRef(surfaceRef, node);
    },
    [surfaceRef],
  );

  useLayoutEffect(() => {
    if (!open) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const layer = registerOverlayLayer({
      lockScroll: true,
      onEscape: () => {
        if (!dismissDisabledRef.current) onCloseRef.current();
      },
      onTab: (event) => trapFocus(event, dialogRef.current),
      restoreFocus: previouslyFocused,
      root: dialogRef.current,
    });
    const dialog = dialogRef.current;
    const initialTarget = focusInitialTarget(dialog);
    const observer = observeInitialFocus(dialog, initialTarget);

    return () => {
      observer?.disconnect();
      layer.release();
    };
  }, [open]);

  if (!present) return null;

  const Surface = as;
  return (
    <OverlayPortal>
      <div className={rootClassName}>
        <button
          aria-label={backdropLabel}
          className={backdropClassName}
          disabled={dismissDisabled}
          onClick={onClose}
          type="button"
        />
        <Surface
          aria-describedby={ariaDescribedBy}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-modal="true"
          className={surfaceClassName}
          onTransitionEnd={onTransitionEnd}
          ref={setSurfaceRef}
          role={role}
          tabIndex={-1}
        >
          {children}
        </Surface>
      </div>
    </OverlayPortal>
  );
}

export function hasOpenOverlayLayers(): boolean {
  return layerStack.length > 0;
}

export function OverlayPortal({ children }: { children: ReactNode }) {
  if (typeof document === "undefined") return children;
  return createPortal(children, document.body);
}

function focusInitialTarget(root: HTMLElement | null): HTMLElement | null {
  const focusable = focusableElements(root);
  const explicit = focusable.find(
    (element) => element.dataset.autofocus === "true" && element.dataset.overlayClose !== "true",
  );
  const firstContentControl = focusable.find((element) => element.dataset.overlayClose !== "true");
  const target = explicit ?? firstContentControl ?? root;
  target?.focus({ preventScroll: true });
  return target;
}

function observeInitialFocus(
  root: HTMLElement | null,
  initialTarget: HTMLElement | null,
): MutationObserver | undefined {
  if (!root || initialTarget !== root || typeof MutationObserver === "undefined") return undefined;
  const observer = new MutationObserver(() => {
    if (document.activeElement !== root) {
      observer.disconnect();
      return;
    }
    const next = focusInitialTarget(root);
    if (next !== root) observer.disconnect();
  });
  observer.observe(root, {
    attributes: true,
    attributeFilter: ["disabled", "tabindex"],
    childList: true,
    subtree: true,
  });
  return observer;
}

function trapFocus(event: KeyboardEvent, root: HTMLElement | null) {
  if (!root) return;
  const focusable = focusableElements(root);
  if (focusable.length === 0) {
    event.preventDefault();
    root.focus({ preventScroll: true });
    return;
  }

  const first = focusable[0];
  const last = focusable.at(-1);
  const active = document.activeElement;
  if (active === root || !root.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first)?.focus({ preventScroll: true });
    return;
  }
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last?.focus({ preventScroll: true });
    return;
  }
  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function focusableElements(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "[tabindex]:not([tabindex='-1'])",
      ].join(","),
    ),
  ).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      element.tabIndex >= 0 &&
      element.getClientRects().length > 0,
  );
}

function assignRef(ref: Ref<HTMLElement> | undefined, node: HTMLElement | null) {
  if (typeof ref === "function") ref(node);
  else if (ref) ref.current = node;
}

/** Test-only: clears layer + lock state between cases. */
export function __resetOverlayStateForTest(): void {
  layerStack.length = 0;
  scrollLockCount = 0;
  savedBodyOverflow = "";
  savedBodyPaddingRight = "";
  if (typeof document !== "undefined") document.removeEventListener("keydown", handleLayerKeyDown);
}
